import { z } from 'zod/v3';
import { LanguageCodeSchema } from '../generated/enums';

// Schema for glossary translations
export const GlossaryTranslationSchema = z.object({
  languageCode: LanguageCodeSchema,
  translatedTerm: z.string(),
  definition: z.string(),
  notes: z.string().optional()
});

// GlossaryTerm DTOs
export const GlossaryTermDTOSchema = z.object({
  id: z.string(),
  term: z.string(),
  definition: z.string(),
  context: z.string().optional(),
  synonyms: z.array(z.string()),
  category: z.string().optional(),
  isReviewed: z.boolean().default(false),
  confidence: z.number().default(0.7),
  translations: z.array(GlossaryTranslationSchema),
  workspaceId: z.string(),
  workspace: z.string(),
  languageCode: LanguageCodeSchema.optional(),
  createdAt: z.date(),
  updatedAt: z.date()
}).strict();

export const GlossaryTermListDTOSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  updatedAt: z.date()
}).strict();

// Schema for creating a glossary term
export const CreateGlossaryTermDTOSchema = z.object({
  term: z.string().min(1),
  definition: z.string().min(1),
  context: z.string().nullable().optional(),
  synonyms: z.array(z.string()).optional(),
  category: z.string().nullable().optional(),
  isReviewed: z.boolean().optional().default(false),
  confidence: z.number().optional().default(0.7),
  translations: z.array(GlossaryTranslationSchema).optional(),
  languageCode: LanguageCodeSchema.optional(),
}).strict();

// Schema for updating a glossary term
export const UpdateGlossaryTermDTOSchema = z.object({
  id: z.string(),
  term: z.string().min(1).optional(),
  definition: z.string().min(1).optional(),
  context: z.string().nullable().optional(),
  synonyms: z.array(z.string()).optional(),
  category: z.string().nullable().optional(),
  isReviewed: z.boolean().optional(),
  confidence: z.number().optional(),
  translations: z.array(GlossaryTranslationSchema).optional(),
}).strict();

// Schema for retrieving a glossary term by ID
export const GetGlossaryTermDTOSchema = z.object({
  id: z.string(),
});

// Schema for retrieving all glossary terms in a workspace
export const GetGlossaryTermsDTOSchema = z.object({
  workspaceId: z.string(),
});

// Schema for deleting a glossary term
export const DeleteGlossaryTermDTOSchema = z.object({
  id: z.string(),
});

// Schema for importing glossary terms
export const ImportGlossaryDTOSchema = z.object({
  terms: z.array(z.object({
    term: z.string(),
    definition: z.string(),
    context: z.string().nullable().optional(),
    synonyms: z.array(z.string()).optional(),
    category: z.string().nullable().optional(),
    isReviewed: z.boolean().optional().default(false),
    confidence: z.number().optional().default(0.7),
    translations: z.array(GlossaryTranslationSchema).optional()
  }))
});

// Schema for extracting terms from content
export const ExtractTermsDTOSchema = z.object({
  content: z.string().min(1),
  existingTerms: z.array(GlossaryTermDTOSchema).optional(),
});

// Schema for translating content
export const TranslateContentDTOSchema = z.object({
  workspaceId: z.string(),
  content: z.string().min(1),
  sourceLanguage: z.string(),
  targetLanguage: z.string(),
});

// Schema for auto-generating glossary terms from workspace sources
export const AutoGenerateTermsDTOSchema = z.object({
  mode: z.enum(['full', 'incremental']).default('full'),
});

// Translation schemas
export const GlossaryTranslationBaseSchema = z.object({
  languageCode: z.string().min(2, 'Language code is required'),
  translatedTerm: z.string().min(1, 'Translated term is required'),
  definition: z.string().min(1, 'Translation definition is required'),
  notes: z.string().optional().nullable(),
});

// Schema for creating a new translation
export const CreateGlossaryTranslationSchema = GlossaryTranslationBaseSchema.extend({
  glossaryTermId: z.string().min(1, 'Glossary Term ID is required'),
});

// Schema for updating an existing translation
export const UpdateGlossaryTranslationSchema = GlossaryTranslationBaseSchema.partial();

// Type exports for DTO schemas
export type GlossaryTermDTO = z.infer<typeof GlossaryTermDTOSchema>;
export type GlossaryTermListDTO = z.infer<typeof GlossaryTermListDTOSchema>;
export type CreateGlossaryTermDTO = z.infer<typeof CreateGlossaryTermDTOSchema>;
export type UpdateGlossaryTermDTO = z.infer<typeof UpdateGlossaryTermDTOSchema>;
export type GetGlossaryTermDTO = z.infer<typeof GetGlossaryTermDTOSchema>;
export type GetGlossaryTermsDTO = z.infer<typeof GetGlossaryTermsDTOSchema>;
export type DeleteGlossaryTermDTO = z.infer<typeof DeleteGlossaryTermDTOSchema>;
export type ImportGlossaryDTO = z.infer<typeof ImportGlossaryDTOSchema>;
export type ExtractTermsDTO = z.infer<typeof ExtractTermsDTOSchema>;
export type TranslateContentDTO = z.infer<typeof TranslateContentDTOSchema>;
export type AutoGenerateTermsDTO = z.infer<typeof AutoGenerateTermsDTOSchema>;
export type GlossaryTranslationDTO = z.infer<typeof GlossaryTranslationSchema>;
export type CreateGlossaryTranslationDTO = z.infer<typeof CreateGlossaryTranslationSchema>;
export type UpdateGlossaryTranslationDTO = z.infer<typeof UpdateGlossaryTranslationSchema>;

// Additional type exports for schema compatibility
export type GlossaryTermWithRelations = GlossaryTermDTO & {
  translations: GlossaryTranslationDTO[];
};

// For component compatibility
export const GlossaryTermUncheckedCreateInputSchema = CreateGlossaryTermDTOSchema;
export const GlossaryTermUncheckedUpdateInputSchema = UpdateGlossaryTermDTOSchema;
export type GlossaryTranslation = GlossaryTranslationDTO;
